import { useMemo } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface ChartDataset {
  label?: string;
  data: number[];
}

interface ParsedChartData {
  labels: string[];
  datasets: ChartDataset[];
}

const CHART_COLORS = [
  "#6E56CF", // puntt-accent-9
  "#30A46C", // malachite / green-9
  "#E5484D", // red-9
  "#F76B15", // amber/orange-9
  "#0090FF", // blue-9
  "#7C66DC", // puntt-accent-10
  "#3CB179", // green-10
  "#DC3D43", // red-10
];

interface ChartBlockProps {
  chartType: "bar" | "pie" | "line";
  data: Record<string, unknown>;
}

function parseChartData(raw: Record<string, unknown>): ParsedChartData | null {
  try {
    const labels = raw.labels;
    const datasets = raw.datasets;

    if (
      !Array.isArray(labels) ||
      labels.length === 0 ||
      !labels.every((l) => typeof l === "string")
    ) {
      return null;
    }

    if (!Array.isArray(datasets) || datasets.length === 0) return null;

    const parsed: ChartDataset[] = (datasets as Record<string, unknown>[]).map(
      (ds) => ({
        label: typeof ds.label === "string" ? ds.label : undefined,
        data: Array.isArray(ds.data) ? (ds.data as unknown[]).map(Number) : [],
      }),
    );

    if (parsed.some((ds) => ds.data.some((n) => !Number.isFinite(n))))
      return null;
    if (parsed.some((ds) => ds.data.length !== labels.length)) return null;

    return { labels: labels as string[], datasets: parsed };
  } catch {
    return null;
  }
}

function toRechartsData(
  parsed: ParsedChartData,
): Record<string, string | number>[] {
  return parsed.labels.map((label, i) => {
    const entry: Record<string, string | number> = { name: label };
    parsed.datasets.forEach((ds, dsIdx) => {
      const key = ds.label ?? `Series ${dsIdx + 1}`;
      entry[key] = ds.data[i];
    });
    return entry;
  });
}

function Placeholder({ chartType }: { chartType: string }) {
  return (
    <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 text-sm text-puntt-neutral-gray-9">
      Chart: {chartType}
    </div>
  );
}

export function ChartBlock({ chartType, data }: ChartBlockProps) {
  const parsed = useMemo(() => parseChartData(data), [data]);

  if (!parsed) return <Placeholder chartType={chartType} />;

  const rechartsData = toRechartsData(parsed);
  const datasetKeys = parsed.datasets.map(
    (ds, i) => ds.label ?? `Series ${i + 1}`,
  );

  return (
    <div className="rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-4 shadow-sm">
      <ResponsiveContainer width="100%" height={280}>
        {chartType === "bar" ? (
          <BarChart
            data={rechartsData}
            margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={{ stroke: "#d1d5db" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #e5e7eb",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                fontSize: 13,
              }}
            />
            {datasetKeys.length > 1 && (
              <Legend wrapperStyle={{ fontSize: 12 }} />
            )}
            {datasetKeys.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                fill={CHART_COLORS[i % CHART_COLORS.length]}
                radius={[4, 4, 0, 0]}
                maxBarSize={48}
              />
            ))}
          </BarChart>
        ) : chartType === "line" ? (
          <LineChart
            data={rechartsData}
            margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={{ stroke: "#d1d5db" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #e5e7eb",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                fontSize: 13,
              }}
            />
            {datasetKeys.length > 1 && (
              <Legend wrapperStyle={{ fontSize: 12 }} />
            )}
            {datasetKeys.map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 4, fill: CHART_COLORS[i % CHART_COLORS.length] }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        ) : (
          <PieChart>
            <Pie
              data={parsed.labels.map((label, i) => ({
                name: label,
                value: parsed.datasets[0].data[i],
              }))}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
              label={({ name, percent }: { name?: string; percent?: number }) =>
                `${name ?? ""} (${((percent ?? 0) * 100).toFixed(0)}%)`
              }
              labelLine={{ stroke: "#9ca3af" }}
            >
              {parsed.labels.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: "1px solid #e5e7eb",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                fontSize: 13,
              }}
            />
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
